import React from 'react';
import PropTypes from 'prop-types';
import T from 'i18n-react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import {
  Box,
  IconButton,
  TablePagination as MuiTablePagination,
  Typography,
} from '@material-ui/core';
import {
  FirstPage as FirstPageIcon,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  LastPage as LastPageIcon,
} from '@material-ui/icons';
import { NativeSelect as Select } from 'components/Form';

const useStyles = makeStyles((theme) => ({
  paginationRoot: {
    flexShrink: 0,
    marginLeft: theme.spacing(2.5),
    marginRight: theme.spacing(2.5),
  },
  pageNumberSelect: {
    padding: theme.spacing(1, 2),
  },
}));

function TablePaginationActions(props) {
  const classes = useStyles();
  const theme = useTheme();
  const { count, page, rowsPerPage, onChangePage } = props;

  const minPageNumber = 0;
  const maxPageNumber = Math.max(0, Math.ceil(count / rowsPerPage) - 1);

  const getPageNumberList = () => {
    const list = [];
    for (let i = minPageNumber; i <= maxPageNumber; i++) {
      list.push({ label: i + 1, value: i });
    }
    return list;
  };

  const handleFirstPageButtonClick = (event) => {
    onChangePage(minPageNumber);
  };

  const handleBackButtonClick = (event) => {
    onChangePage(page - 1);
  };

  const handleNextButtonClick = (event) => {
    onChangePage(page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onChangePage(maxPageNumber);
  };

  const handleChangePageNumber = (event) => {
    let targetValue = Number(event.target.value);

    // case: user input lower and larger than min or max page => force to min or max limit
    if (targetValue < minPageNumber) targetValue = minPageNumber;
    if (targetValue > maxPageNumber) targetValue = maxPageNumber;

    onChangePage(targetValue);
  };

  return (
    <React.Fragment>
      <Box pl={4} display='flex' alignItems='center'>
        <Typography variant='body2'>{T.translate('general.page')}</Typography>
        <Select
          options={getPageNumberList()}
          selectProps={{
            classes: { root: classes.pageNumberSelect },
            value: page,
            onChange: handleChangePageNumber,
          }}
        />
      </Box>
      <div className={classes.paginationRoot}>
        <IconButton
          onClick={handleFirstPageButtonClick}
          disabled={page === 0}
          aria-label='first page'>
          {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
        </IconButton>
        <IconButton
          onClick={handleBackButtonClick}
          disabled={page === 0}
          aria-label='previous page'>
          {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
        </IconButton>
        <IconButton
          onClick={handleNextButtonClick}
          disabled={page >= Math.ceil(count / rowsPerPage) - 1}
          aria-label='next page'>
          {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
        </IconButton>
        <IconButton
          onClick={handleLastPageButtonClick}
          disabled={page >= Math.ceil(count / rowsPerPage) - 1}
          aria-label='last page'>
          {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
        </IconButton>
      </div>
    </React.Fragment>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onChangePage: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

const TablePagination = (props) => {
  const {
    data: { columnData, page, rowsPerPage, rowsPerPageOptions, tableData },
    func: { onChangePage, onChangeRowsPerPage },
    classes,
  } = props;

  return (
    <Box width='100%' display='flex' justifyContent='flex-end' paddingTop='36px'>
      <MuiTablePagination
        classes={classes}
        rowsPerPageOptions={rowsPerPageOptions} // { label: "All", value: -1 }
        colSpan={columnData.length}
        count={tableData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        labelRowsPerPage={T.translate('general.rowsPerPage')}
        SelectProps={{
          inputProps: { 'aria-label': T.translate('general.rowsPerPage') },
          native: true,
        }}
        onChangePage={onChangePage}
        onChangeRowsPerPage={onChangeRowsPerPage}
        ActionsComponent={TablePaginationActions}
        style={{
          width: '100%',
        }}
      />
    </Box>
  );
};

TablePagination.defaultProps = {
  columnData: [],
  page: 0,
  rowsPerPage: 10,
  rowsPerPageOptions: [],
  tableData: [],
  onChangePage: () => {},
  onChangeRowsPerPage: () => {},
};

TablePagination.propTypes = {
  columnData: PropTypes.arrayOf(PropTypes.object),
  data: PropTypes.object.isRequired,
  func: PropTypes.object.isRequired,
  page: PropTypes.number,
  rowsPerPage: PropTypes.number,
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  tableData: PropTypes.arrayOf(PropTypes.object),
  onChangePage: PropTypes.func,
  onChangeRowsPerPage: PropTypes.func,
};

export default TablePagination;
